import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { CRM_WA_TEMPLATES } from '../../../services/crmService';

const normalizePhone = (phone) => {
  if (!phone) return '';
  return phone?.replace(/[\s\-\(\)\+]/g, '')?.replace(/\D/g, '');
};

const buildWaUrl = (phone, message) => {
  const normalized = normalizePhone(phone);
  if (!normalized) return null;
  return `https://wa.me/${normalized}?text=${encodeURIComponent(message)}`;
};

const WhatsAppModal = ({ contacts, initialMessage = '', onClose, onSaveActivity, companyId }) => {
  const [message, setMessage] = useState(initialMessage);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sending, setSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [errors, setErrors] = useState([]);

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    if (!templateId) return;
    const tpl = CRM_WA_TEMPLATES?.find(t => t?.id === templateId);
    if (tpl) {
      const firstContact = contacts?.[0];
      const text = tpl?.texto?.replace('{nombre}', firstContact?.nombre || 'cliente');
      setMessage(text);
    }
  };

  const getMessageForContact = (contact) => {
    if (selectedTemplate) {
      const tpl = CRM_WA_TEMPLATES?.find(t => t?.id === selectedTemplate);
      if (tpl) return tpl?.texto?.replace('{nombre}', contact?.nombre || 'cliente');
    }
    return message;
  };

  const sendToContact = async (contact, msg) => {
    const phone = contact?.whatsapp || contact?.telefono;
    const url = buildWaUrl(phone, msg);
    if (!url) {
      return { success: false, contact, error: 'Sin número registrado' };
    }
    window.open(url, '_blank');

    // Save activity
    try {
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const hora = new Date()?.toTimeString()?.slice(0, 5);
      await onSaveActivity({
        contact_id: contact?.id,
        company_id: companyId,
        tipo: 'whatsapp',
        fecha: today,
        hora,
        notas: msg,
        estado: 'realizado',
      });
    } catch (err) {
      console.error('Error saving WA activity:', err);
    }

    return { success: true, contact };
  };

  const handleSendAll = async () => {
    if (!message?.trim()) return;
    setSending(true);
    setSentCount(0);
    setErrors([]);
    const errs = [];

    for (let i = 0; i < contacts?.length; i++) {
      const contact = contacts?.[i];
      const msg = getMessageForContact(contact);
      const result = await sendToContact(contact, msg);
      if (result?.success) {
        setSentCount(prev => prev + 1);
      } else {
        errs?.push(`${contact?.nombre}: ${result?.error}`);
      }
      if (i < contacts?.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 800));
      }
    }

    setErrors(errs);
    setSending(false);
  };

  const handleSendSingle = async () => {
    if (!message?.trim() || contacts?.length !== 1) return;
    setSending(true);
    const contact = contacts?.[0];
    const msg = getMessageForContact(contact);
    const result = await sendToContact(contact, msg);
    if (!result?.success) {
      setErrors([`${contact?.nombre}: ${result?.error}`]);
    } else {
      setSentCount(1);
    }
    setSending(false);
  };

  const isSingle = contacts?.length === 1;
  const allDone = sentCount === contacts?.length && sentCount > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <Icon name="MessageCircle" size={16} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Enviar WhatsApp</h2>
              <p className="text-xs text-muted-foreground">
                {isSingle ? contacts?.[0]?.nombre : `${contacts?.length} contactos seleccionados`}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Contact list (bulk) */}
          {!isSingle && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Contactos</label>
              <div className="bg-muted/40 rounded-lg p-3 max-h-32 overflow-y-auto space-y-1">
                {contacts?.map(c => {
                  const phone = c?.whatsapp || c?.telefono;
                  const hasPhone = !!normalizePhone(phone);
                  return (
                    <div key={c?.id} className="flex items-center justify-between text-sm">
                      <span className="text-foreground">{c?.nombre}</span>
                      {hasPhone ? (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <Icon name="Check" size={10} /> {phone}
                        </span>
                      ) : (
                        <span className="text-xs text-red-500 flex items-center gap-1">
                          <Icon name="AlertCircle" size={10} /> Sin número registrado
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Template selector */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Plantilla</label>
            <select
              value={selectedTemplate}
              onChange={e => handleTemplateChange(e?.target?.value)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">— Mensaje manual —</option>
              {CRM_WA_TEMPLATES?.map(t => (
                <option key={t?.id} value={t?.id}>{t?.nombre}</option>
              ))}
            </select>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Mensaje
              {!isSingle && selectedTemplate && (
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  (se personalizará con el nombre de cada contacto)
                </span>
              )}
            </label>
            <textarea
              value={message}
              onChange={e => setMessage(e?.target?.value)}
              rows={4}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              placeholder="Escribe tu mensaje aquí..."
            />
            <p className="text-xs text-muted-foreground mt-1">{message?.length} caracteres</p>
          </div>

          {/* Errors */}
          {errors?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-xs font-medium text-red-700 mb-1">Errores:</p>
              {errors?.map((e, i) => (
                <p key={i} className="text-xs text-red-600">{e}</p>
              ))}
            </div>
          )}

          {/* Success */}
          {allDone && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
              <Icon name="CheckCircle" size={16} className="text-green-600" />
              <p className="text-sm text-green-700 font-medium">
                {sentCount === 1 ? 'Mensaje enviado correctamente' : `${sentCount} mensajes enviados correctamente`}
              </p>
            </div>
          )}

          {/* Progress */}
          {sending && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              Enviando {sentCount + 1} de {contacts?.length}...
            </div>
          )}
        </div>

        <div className="flex gap-3 p-5 border-t border-border">
          <Button type="button" variant="outline" className="flex-1" onClick={onClose} disabled={sending}>
            {allDone ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!allDone && (
            <Button
              type="button"
              variant="default"
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={sending || !message?.trim()}
              onClick={isSingle ? handleSendSingle : handleSendAll}
            >
              <Icon name="MessageCircle" size={14} className="mr-1.5" />
              {isSingle ? 'Enviar WhatsApp' : `Enviar a ${contacts?.length} contactos`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppModal;
